<script setup lang="ts">
/**
 * Right cell for the mobile payments table.
 *
 * Renders the amount on the primary line (monospace, sign-colored) with a
 * "+" prefix for positive amounts, and the time of day on the secondary
 * line — the date itself is shown once per group in the sticky date header
 * (`DateHeaderRowCell`) rather than repeated on every row. Right-aligned to
 * contrast with the name/status block in the left cell.
 */
import { computed } from 'vue'
import { formatMoney, currencySymbol } from '@/utils/formatMoney'

const props = withDefaults(
  defineProps<{
    amount?: number | null
    time: string
    width?: string
  }>(),
  {},
)

const hasAmount = computed(
  () => props.amount !== undefined && props.amount !== null && !Number.isNaN(props.amount),
)

const amountDisplay = computed(() => {
  if (!hasAmount.value) return null
  const raw = props.amount!
  const { formatted } = formatMoney({ amount: raw })
  const prefix = raw > 0 ? '+' : ''
  return `${prefix}${formatted} ${currencySymbol}`
})

const amountStyle = computed(() => {
  if (!hasAmount.value) return {}
  const raw = props.amount!
  if (raw < 0) return { color: 'var(--color-money-negative)' }
  if (raw > 0) return { color: 'var(--color-money-positive)' }
  return { color: 'var(--color-tufte-muted)' }
})
</script>

<template>
  <td class="payment-mobile-right font-tufte text-tufte-text">
    <div class="right-layout">
      <span v-if="amountDisplay" class="amount" :style="amountStyle">{{ amountDisplay }}</span>
      <span v-else class="amount text-tufte-muted">–</span>
      <span class="date text-tufte-muted">{{ time }}</span>
    </div>
  </td>
</template>

<style scoped>
.payment-mobile-right {
  vertical-align: top;
}

.right-layout {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.15rem;
}

.amount {
  font-family: var(--font-tufte-mono);
  font-size: var(--text-tufte-lg);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  line-height: 1.2;
}

.date {
  font-family: var(--font-tufte-sans);
  font-size: var(--text-tufte-xs);
  white-space: nowrap;
  line-height: var(--line-height-tufte-normal);
}
</style>
